import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Globe,
  Upload,
  FileCode,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
  FileText,
  ShoppingBag,
  Briefcase,
  Layers,
  RefreshCw,
  Search,
  Check,
  X,
  ExternalLink,
  Image as ImageIcon,
  CheckSquare,
  Square
} from 'lucide-react';

interface WPImportItem {
  id: string;
  title: string;
  slug: string;
  content: string;
  summary: string;
  categoryName: string;
  author: string;
  coverImage: string;
  createdAt: string;
  selected: boolean;
  type?: 'post' | 'photo' | 'page' | 'attachment';
  caption?: string;
  url?: string;
}

export default function WordPressImporter() {
  const { categories, refreshData, showToast } = useApp();

  const [activeMode, setActiveMode] = useState<'api' | 'xml'>('xml');
  const [wpUrl, setWpUrl] = useState('');
  const [contentType, setContentType] = useState<'posts' | 'pages' | 'media'>('posts');
  const [targetCollection, setTargetCollection] = useState<'articles' | 'gallery' | 'products' | 'portfolio'>('articles');
  const [autoCreateCategories, setAutoCreateCategories] = useState(true);
  const [cleanShortcodes, setCleanShortcodes] = useState(true);

  // States for Content Lists
  const [isFetching, setIsFetching] = useState(false);
  const [wpStats, setWpStats] = useState<{ totalItems: number; totalPages: number; totalPosts?: number; totalPhotos?: number } | null>(null);
  const [postsList, setPostsList] = useState<WPImportItem[]>([]);
  const [photosList, setPhotosList] = useState<WPImportItem[]>([]);
  const [activeViewTab, setActiveViewTab] = useState<'posts' | 'photos'>('posts');
  const [searchQuery, setSearchQuery] = useState('');

  // XML Upload State
  const [xmlFileName, setXmlFileName] = useState<string | null>(null);

  // Import Execution States
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [importResult, setImportResult] = useState<{ successCount: number; failedCount: number } | null>(null);

  // Active items list depending on tab
  const activeItems = activeViewTab === 'posts' ? postsList : photosList;
  const setActiveItems = activeViewTab === 'posts' ? setPostsList : setPhotosList;

  // --------------------------------------------------------------------------
  // REST API Fetcher
  // --------------------------------------------------------------------------
  const handleFetchFromApi = async () => {
    if (!wpUrl.trim()) {
      showToast('Masukkan URL website WordPress terlebih dahulu', 'warning');
      return;
    }

    setIsFetching(true);
    setWpStats(null);
    setPostsList([]);
    setPhotosList([]);
    setImportResult(null);

    try {
      const response = await fetch('/api/admin/wordpress-fetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: wpUrl.trim(),
          type: contentType,
          page: 1,
          perPage: 100,
        }),
      });

      const resData = await response.json();

      if (!response.ok || !resData.success) {
        throw new Error(resData.message || 'Gagal terhubung ke WordPress API');
      }

      const fetchedItems: WPImportItem[] = (resData.data || []).map((item: any, idx: number) => ({
        id: item.id || String(Date.now() + idx),
        title: item.title || 'Tanpa Judul',
        slug: item.slug || '',
        content: item.content || '',
        summary: item.summary || item.title || '',
        categoryName: item.categoryName || 'Umum',
        author: item.author || 'Admin WP',
        coverImage: item.coverImage || '',
        url: item.coverImage || '',
        createdAt: item.createdAt || new Date().toISOString(),
        selected: true,
        type: contentType === 'media' ? 'photo' : 'post'
      }));

      if (contentType === 'media') {
        setPhotosList(fetchedItems);
        setActiveViewTab('photos');
        setTargetCollection('gallery');
      } else {
        setPostsList(fetchedItems);
        setActiveViewTab('posts');
        setTargetCollection('articles');
      }

      setWpStats({
        totalItems: resData.totalItems || fetchedItems.length,
        totalPages: resData.totalPages || 1,
        totalPosts: contentType !== 'media' ? fetchedItems.length : 0,
        totalPhotos: contentType === 'media' ? fetchedItems.length : 0,
      });

      showToast(
        `Berhasil mengambil ${fetchedItems.length} konten dari WordPress API (${resData.totalItems || fetchedItems.length} total)`,
        'success'
      );
    } catch (err: any) {
      console.error('WP Fetch Error:', err);
      showToast(err.message || 'Gagal terhubung ke WordPress API', 'error');
    } finally {
      setIsFetching(false);
    }
  };

  // --------------------------------------------------------------------------
  // XML File Parser (Server-side + Client fallback)
  // --------------------------------------------------------------------------
  const handleXmlFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xml') && !file.name.toLowerCase().endsWith('.wxr')) {
      showToast('Harap pilih file ekspor WordPress dengan ekstensi .xml atau .wxr', 'warning');
      return;
    }

    setXmlFileName(file.name);
    setIsFetching(true);
    setImportResult(null);
    setPostsList([]);
    setPhotosList([]);

    // 1. Send via FormData to server parser first
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/admin/wordpress-upload-xml', {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          const parsedPosts: WPImportItem[] = (json.posts || []).map((p: any) => ({
            ...p,
            selected: true,
            type: 'post'
          }));
          const parsedPhotos: WPImportItem[] = (json.photos || []).map((ph: any) => ({
            ...ph,
            selected: true,
            type: 'photo',
            coverImage: ph.url,
            summary: ph.caption || ph.title
          }));

          setPostsList(parsedPosts);
          setPhotosList(parsedPhotos);

          if (parsedPosts.length > 0) {
            setActiveViewTab('posts');
            setTargetCollection('articles');
          } else if (parsedPhotos.length > 0) {
            setActiveViewTab('photos');
            setTargetCollection('gallery');
          }

          setWpStats({
            totalItems: parsedPosts.length + parsedPhotos.length,
            totalPages: 1,
            totalPosts: parsedPosts.length,
            totalPhotos: parsedPhotos.length
          });

          showToast(
            `File XML berhasil diproses! Ditemukan ${parsedPosts.length} artikel dan ${parsedPhotos.length} foto/media.`,
            'success'
          );
          setIsFetching(false);
          return;
        }
      }
    } catch (uploadErr) {
      console.warn('Server XML upload failed, trying local fallback:', uploadErr);
    }

    // 2. Client-side Regex Fallback
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const xmlText = evt.target?.result as string;
        if (!xmlText) throw new Error('File kosong');

        const attachments = new Map<string, string>();
        const photos: WPImportItem[] = [];
        const posts: WPImportItem[] = [];

        const itemRegex = /<item>([\s\S]*?)<\/item>/gi;
        let match;

        const extract = (str: string, tag: string) => {
          const r = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'i');
          const m = str.match(r);
          if (!m) return '';
          let val = m[1].trim();
          if (val.startsWith('<![CDATA[') && val.endsWith(']]>')) {
            val = val.substring(9, val.length - 3);
          }
          return val.trim();
        };

        while ((match = itemRegex.exec(xmlText)) !== null) {
          const itemStr = match[1];
          const postType = extract(itemStr, 'wp:post_type') || 'post';
          const postId = extract(itemStr, 'wp:post_id') || String(Date.now() + Math.random());
          const title = extract(itemStr, 'title') || 'Tanpa Judul';
          const pubDate = extract(itemStr, 'wp:post_date') || extract(itemStr, 'pubDate');
          const postDate = pubDate ? new Date(pubDate).toISOString() : new Date().toISOString();

          if (postType === 'attachment') {
            const url = extract(itemStr, 'wp:attachment_url') || extract(itemStr, 'guid');
            if (url && url.startsWith('http')) {
              attachments.set(postId, url);
              photos.push({
                id: `gal-wp-${postId}`,
                title: title || 'Dokumentasi & Galeri Foto',
                slug: `foto-${postId}`,
                content: '',
                summary: extract(itemStr, 'excerpt:encoded') || title,
                categoryName: 'Galeri Media',
                author: 'Admin Redaksi',
                coverImage: url,
                url: url,
                createdAt: postDate,
                selected: true,
                type: 'photo'
              });
            }
            continue;
          }

          if (postType === 'post' || postType === 'page') {
            const rawContent = extract(itemStr, 'content:encoded');
            const summary = extract(itemStr, 'excerpt:encoded') || rawContent.replace(/<[^>]*>/g, '').substring(0, 180) + '...';
            const catName = extract(itemStr, 'category') || 'Umum';

            let cover = '';
            const thumbMatch = itemStr.match(/<wp:meta_key><!\[CDATA\[_thumbnail_id\]\]><\/wp:meta_key>\s*<wp:meta_value><!\[CDATA\[(\d+)\]\]><\/wp:meta_value>/i);
            if (thumbMatch && attachments.has(thumbMatch[1])) {
              cover = attachments.get(thumbMatch[1])!;
            } else {
              const imgM = rawContent.match(/<img[^>]+src=["']([^"']+)["']/i);
              cover = imgM ? imgM[1] : 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80';
            }

            posts.push({
              id: `art-wp-${postId}`,
              title,
              slug: extract(itemStr, 'wp:post_name') || title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
              content: rawContent || `<p>${summary}</p>`,
              summary: summary || title,
              categoryName: catName,
              author: 'Admin Redaksi',
              coverImage: cover,
              createdAt: postDate,
              selected: true,
              type: 'post'
            });
          }
        }

        setPostsList(posts);
        setPhotosList(photos);

        if (posts.length > 0) {
          setActiveViewTab('posts');
          setTargetCollection('articles');
        } else if (photos.length > 0) {
          setActiveViewTab('photos');
          setTargetCollection('gallery');
        }

        setWpStats({
          totalItems: posts.length + photos.length,
          totalPages: 1,
          totalPosts: posts.length,
          totalPhotos: photos.length
        });

        showToast(`Berhasil membaca ${posts.length} artikel dan ${photos.length} foto dari file XML.`, 'success');
      } catch (clientErr: any) {
        console.error('Client XML parser error:', clientErr);
        showToast(`Gagal membaca file XML: ${clientErr.message}`, 'error');
      } finally {
        setIsFetching(false);
      }
    };

    reader.readAsText(file);
  };

  // Toggle Selection
  const toggleSelectItem = (id: string) => {
    setActiveItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, selected: !item.selected } : item))
    );
  };

  const toggleSelectAll = (select: boolean) => {
    setActiveItems((prev) => prev.map((item) => ({ ...item, selected: select })));
  };

  // Filtered items
  const filteredItems = activeItems.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.categoryName && item.categoryName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedCount = activeItems.filter((item) => item.selected).length;

  // --------------------------------------------------------------------------
  // Execute Bulk Import into Database
  // --------------------------------------------------------------------------
  const handleExecuteImport = async () => {
    const selectedItems = activeItems.filter((item) => item.selected);
    if (selectedItems.length === 0) {
      showToast('Harap pilih minimal satu konten yang akan diimpor', 'warning');
      return;
    }

    setIsImporting(true);
    setImportProgress({ current: 0, total: selectedItems.length });
    setImportResult(null);

    try {
      const response = await fetch('/api/admin/wordpress-bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetCollection,
          items: selectedItems,
          autoCreateCategories,
          cleanShortcodes,
        }),
      });

      const resData = await response.json();

      if (!response.ok || !resData.success) {
        throw new Error(resData.message || 'Gagal melakukan impor massal ke database');
      }

      setImportResult({
        successCount: resData.importedCount || selectedItems.length,
        failedCount: 0,
      });

      // Refresh dashboard state
      await refreshData();
      showToast(
        resData.message ||
          `Proses impor selesai! ${selectedItems.length} konten berhasil dimasukkan ke sistem.`,
        'success'
      );
    } catch (err: any) {
      console.error('Import error:', err);
      showToast(err.message || 'Terjadi kesalahan saat mengimpor', 'error');
      setImportResult({
        successCount: 0,
        failedCount: selectedItems.length,
      });
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Top Header Card */}
      <div className="bg-gradient-to-r from-emerald-900 via-slate-900 to-emerald-950 text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-emerald-800/40 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/30">
              <Globe className="w-3.5 h-3.5" />
              <span>Migrasi & Pengambil Konten Otomatis</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
              Impor Konten dari Website WordPress
            </h2>
            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
              Impor artikel, berita, postingan, dan <strong>foto-foto galeri media</strong> dari website WordPress Anda secara lengkap dan utuh via file ekspor XML (.wxr) atau online via WordPress REST API.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setWpUrl('');
                setPostsList([]);
                setPhotosList([]);
                setWpStats(null);
                setImportResult(null);
                setXmlFileName(null);
              }}
              className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-all flex items-center gap-2 border border-white/10"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset Form</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mode Selection Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-2 shadow-2xs flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => {
            setActiveMode('xml');
          }}
          className={`flex-1 py-3 px-5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2.5 ${
            activeMode === 'xml'
              ? 'bg-emerald-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
        >
          <Upload className="w-4 h-4" />
          <span>Unggah File XML WordPress Export (.xml / .wxr)</span>
        </button>

        <button
          onClick={() => {
            setActiveMode('api');
          }}
          className={`flex-1 py-3 px-5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2.5 ${
            activeMode === 'api'
              ? 'bg-emerald-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
        >
          <Globe className="w-4 h-4" />
          <span>Hubungkan via WordPress REST API (Online)</span>
        </button>
      </div>

      {/* Mode 1: XML Export Parser */}
      {activeMode === 'xml' && (
        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-2xs space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-black">
              <FileCode className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-slate-900 text-base">
                Unggah File Ekspor XML dari WordPress
              </h3>
              <p className="text-xs text-slate-500">
                Mendukung file ekspor <strong>POSTINGAN.xml</strong> (artikel & konten) dan <strong>MEDIA.xml</strong> (foto & galeri gambar).
              </p>
            </div>
          </div>

          <div className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-3xl p-8 text-center transition-all bg-slate-50/50 hover:bg-emerald-50/20 group">
            <input
              type="file"
              accept=".xml,.wxr"
              onChange={handleXmlFileUpload}
              className="hidden"
              id="wp-xml-upload"
            />
            <label htmlFor="wp-xml-upload" className="cursor-pointer space-y-3 block">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                <Upload className="w-8 h-8" />
              </div>
              <div>
                <p className="font-extrabold text-slate-900 text-sm">
                  {xmlFileName ? `File Terpilih: ${xmlFileName}` : 'Klik untuk Memilih File .XML WordPress Export'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Mendukung file XML postingan, media foto, atau gabungan semua konten dari WordPress
                </p>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Mode 2: REST API Connector */}
      {activeMode === 'api' && (
        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-2xs space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-black">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-slate-900 text-base">
                Hubungkan ke WordPress REST API Sumber
              </h3>
              <p className="text-xs text-slate-500">
                Sistem akan memanggil endpoint `/wp-json/wp/v2/` untuk mengambil artikel atau media foto secara langsung.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-7">
              <label className="block text-xs font-bold text-slate-700 mb-2">
                URL Website WordPress (Contoh: domain-anda.com atau https://domain-anda.com)
              </label>
              <div className="relative">
                <Globe className="w-5 h-5 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  value={wpUrl}
                  onChange={(e) => setWpUrl(e.target.value)}
                  placeholder="https://domain-anda.com"
                  className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 text-sm font-medium transition-all"
                />
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-bold text-slate-700 mb-2">
                Tipe Konten
              </label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value as any)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-600 text-sm font-medium transition-all bg-white"
              >
                <option value="posts">Posting / Artikel (Posts)</option>
                <option value="media">Foto & Media Unggahan (Media)</option>
                <option value="pages">Halaman Statis (Pages)</option>
              </select>
            </div>

            <div className="md:col-span-2 flex items-end">
              <button
                onClick={handleFetchFromApi}
                disabled={isFetching}
                className="w-full py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-extrabold text-xs shadow-md transition-all flex items-center justify-center gap-2 h-[42px]"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Menghubungkan...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>Ambil Konten</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Summary Banner if loaded */}
      {wpStats && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-extrabold text-emerald-950 text-sm">
                Data WordPress Ditemukan & Siap Diimpor
              </h4>
              <p className="text-xs text-emerald-800">
                Ditemukan <strong>{wpStats.totalPosts || postsList.length}</strong> artikel dan <strong>{wpStats.totalPhotos || photosList.length}</strong> foto/media. Pilih konten yang ingin dimasukkan ke sistem.
              </p>
            </div>
          </div>

          {/* Tab Switcher between Articles and Photos */}
          <div className="flex items-center gap-2 bg-white p-1 rounded-xl border border-emerald-200 shadow-2xs">
            <button
              onClick={() => {
                setActiveViewTab('posts');
                setTargetCollection('articles');
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeViewTab === 'posts' ? 'bg-emerald-600 text-white shadow-2xs' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Artikel ({postsList.length})</span>
            </button>

            <button
              onClick={() => {
                setActiveViewTab('photos');
                setTargetCollection('gallery');
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeViewTab === 'photos' ? 'bg-emerald-600 text-white shadow-2xs' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <ImageIcon className="w-3.5 h-3.5" />
              <span>Foto/Media ({photosList.length})</span>
            </button>
          </div>
        </div>
      )}

      {/* Preview Table & Target Configuration */}
      {activeItems.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-2xs space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-100">
            <div>
              <h3 className="font-extrabold text-slate-900 text-base">
                Tentukan Target Koleksi Penyimpanan
              </h3>
              <p className="text-xs text-slate-500">
                Pilih kemanakah konten terpilih ini ingin Anda tempatkan di dalam website.
              </p>
            </div>

            {/* Target Collection Switcher */}
            <div className="flex flex-wrap items-center gap-2 bg-slate-100 p-1.5 rounded-2xl border border-slate-200">
              <button
                type="button"
                onClick={() => setTargetCollection('articles')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 ${
                  targetCollection === 'articles'
                    ? 'bg-emerald-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span>Simpan ke Artikel & Berita</span>
              </button>

              <button
                type="button"
                onClick={() => setTargetCollection('gallery')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 ${
                  targetCollection === 'gallery'
                    ? 'bg-emerald-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Simpan ke Galeri Foto</span>
              </button>

              <button
                type="button"
                onClick={() => setTargetCollection('products')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 ${
                  targetCollection === 'products'
                    ? 'bg-emerald-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                <span>Simpan ke Layanan Hukum</span>
              </button>

              <button
                type="button"
                onClick={() => setTargetCollection('portfolio')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 ${
                  targetCollection === 'portfolio'
                    ? 'bg-emerald-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Briefcase className="w-3.5 h-3.5" />
                <span>Simpan ke Portofolio Kasus</span>
              </button>
            </div>
          </div>

          {/* Additional Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoCreateCategories}
                onChange={(e) => setAutoCreateCategories(e.target.checked)}
                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
              />
              <span className="text-xs font-bold text-slate-800">
                Sinkronkan & Buat Kategori Baru Otomatis (jika belum ada)
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cleanShortcodes}
                onChange={(e) => setCleanShortcodes(e.target.checked)}
                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
              />
              <span className="text-xs font-bold text-slate-800">
                Bersihkan Shortcode WordPress (`[caption]`, `[gallery]`, dll)
              </span>
            </label>
          </div>

          {/* Search & Bulk Select Controls */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleSelectAll(true)}
                className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all flex items-center gap-1"
              >
                <CheckSquare className="w-3.5 h-3.5" />
                <span>Pilih Semua ({activeItems.length})</span>
              </button>
              <button
                type="button"
                onClick={() => toggleSelectAll(false)}
                className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all flex items-center gap-1"
              >
                <Square className="w-3.5 h-3.5" />
                <span>Kosongkan Pilihan</span>
              </button>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari judul / kategori..."
                className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-slate-300 focus:border-emerald-600 transition-all"
              />
            </div>
          </div>

          {/* Items Table */}
          <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
            <div className="max-h-[440px] overflow-y-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-slate-100 text-slate-700 font-extrabold sticky top-0 z-10 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4 w-10 text-center">#</th>
                    <th className="py-3 px-4 w-16">Foto / Media</th>
                    <th className="py-3 px-4">Judul & Cuplikan Konten</th>
                    <th className="py-3 px-4 w-32">Kategori</th>
                    <th className="py-3 px-4 w-28">Tanggal</th>
                    <th className="py-3 px-4 w-20 text-center">Pilih</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-400">
                        Tidak ada konten yang sesuai dengan pencarian
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item, idx) => (
                      <tr
                        key={item.id}
                        onClick={() => toggleSelectItem(item.id)}
                        className={`hover:bg-emerald-50/40 transition-colors cursor-pointer ${
                          item.selected ? 'bg-emerald-50/20' : 'opacity-60 bg-slate-50/50'
                        }`}
                      >
                        <td className="py-3 px-4 text-center font-mono text-slate-400">
                          {idx + 1}
                        </td>
                        <td className="py-3 px-4">
                          <img
                            src={
                              item.coverImage ||
                              item.url ||
                              'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=200&q=80'
                            }
                            alt={item.title}
                            className="w-12 h-10 object-cover rounded-lg border border-slate-200 bg-slate-100 shadow-2xs"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=200&q=80';
                            }}
                          />
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-bold text-slate-900 line-clamp-1">{item.title}</p>
                          <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                            {item.summary || (item.content ? item.content.replace(/<[^>]*>/g, '').substring(0, 100) : 'Tanpa ringkasan')}
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-md text-[10px] font-bold">
                            {item.categoryName || 'Umum'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                          {new Date(item.createdAt).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={item.selected}
                            onChange={() => toggleSelectItem(item.id)}
                            className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Footer */}
          <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-slate-500">
              Menampilkan <strong>{filteredItems.length}</strong> konten. <strong>{selectedCount}</strong> konten dipilih untuk diimpor ke <strong>{targetCollection === 'articles' ? 'Artikel & Berita' : targetCollection === 'gallery' ? 'Galeri Foto' : targetCollection === 'products' ? 'Layanan' : 'Portofolio'}</strong>.
            </div>

            <button
              onClick={handleExecuteImport}
              disabled={isImporting || selectedCount === 0}
              className="py-3 px-7 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-extrabold text-sm shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              {isImporting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>
                    Mengimpor ({importProgress?.current || 0}/{importProgress?.total || selectedCount})...
                  </span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span>Impor {selectedCount} Konten Terpilih Sekarang</span>
                </>
              )}
            </button>
          </div>

          {/* Import Result Notification */}
          {importResult && (
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-bold flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span>
                  Proses impor selesai! <strong>{importResult.successCount}</strong> konten berhasil diimpor ke dalam website.
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
